import React, { useState, useEffect } from 'react';
import './IconSelector.css';
import { fetchIcon } from '../../utils/icons';

interface AvailableIcon {
    name: string;
    url: string;
    displayName: string;
}

interface IconSelectorProps {
    availableIcons: AvailableIcon[];
    selectedIcon: string;
    onIconSelect: (iconName: string) => void;
    label?: string;
}

const IconSelector: React.FC<IconSelectorProps> = ({
    availableIcons,
    selectedIcon,
    onIconSelect,
    label = "Select Icon"
}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [iconUrls, setIconUrls] = useState<{ [key: string]: string }>({});

    // fetch icon URLs when availableIcons change
    useEffect(() => {
        const fetchIconUrls = async () => {
            const urlMap: { [key: string]: string } = {};
            for (const icon of availableIcons) {
                try {
                    const iconUrl = await fetchIcon(icon.name);
                    if (iconUrl) {
                        urlMap[icon.name] = iconUrl;
                    }
                } catch (error) {
                    console.error(`Failed to fetch icon ${icon.name}:`, error);
                }
            }
            setIconUrls(urlMap);
        };

        if (availableIcons.length > 0) {
            fetchIconUrls();
        }
    }, [availableIcons]);

    const filteredIcons = availableIcons.filter(icon =>
        icon.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        icon.displayName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const selectedIconData = availableIcons.find(icon => icon.name === selectedIcon);

    const handleIconSelect = (iconName: string) => {
        onIconSelect(iconName);
        setIsDropdownOpen(false);
        setSearchTerm('');
    };

    return (
        <div className="icon-selector">
            <label className="icon-selector-label">{label}</label>

            <div className="icon-selector-container">
                <div
                    className="icon-selector-trigger"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                    <div className="selected-icon-display">
                        {selectedIconData && (
                            <>
                                <img
                                    src={iconUrls[selectedIconData.name] || ''}
                                    alt={selectedIconData.displayName}
                                    className="selected-icon-image"
                                />
                                <span className="selected-icon-name">
                                    {selectedIconData.displayName}
                                </span>
                            </>
                        )}
                        {!selectedIconData && (
                            <span className="no-icon-selected">No icon selected</span>
                        )}
                    </div>
                    <span className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}>
                        ▼
                    </span>
                </div>

                {isDropdownOpen && (
                    <div className="icon-dropdown">
                        <div className="icon-search">
                            <input
                                type="text"
                                placeholder="Search icons..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="icon-search-input"
                            />
                        </div>

                        <div className="icons-grid">
                            {filteredIcons.map(icon => (
                                <div
                                    key={icon.name}
                                    className={`icon-option ${selectedIcon === icon.name ? 'selected' : ''}`}
                                    onClick={() => handleIconSelect(icon.name)}
                                    title={icon.displayName}
                                >
                                    <img
                                        src={iconUrls[icon.name] || ''}
                                        alt={icon.displayName}
                                        className="icon-option-image"
                                    />
                                    <span className="icon-option-name">
                                        {icon.displayName}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {filteredIcons.length === 0 && (
                            <div className="no-icons-found">
                                No icons found matching "{searchTerm}"
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default IconSelector; 